import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '@t-one-internal/design-system';

interface TeamMemberGoal {
    userId: string | number;
    employeeName: string;
    avgProgress: number;
    totalGoals: number;
    onTrackGoals: number;
    atRiskGoals: number;
}

interface MyTeamGoalsListProps {
    data: Array<Record<string, unknown>>;
    cycleId?: string;
}

const toTeamMemberGoal = (raw: Record<string, unknown>): TeamMemberGoal => ({
    userId: (raw.userId as string | number) ?? '',
    employeeName: (raw.employeeName as string) ?? 'Unknown',
    avgProgress: Number(raw.avgProgress ?? 0),
    totalGoals: Number(raw.totalGoals ?? 0),
    onTrackGoals: Number(raw.onTrackGoals ?? 0),
    atRiskGoals: Number(raw.atRiskGoals ?? 0),
});

const getProgressColor = (progress: number): string => {
    if (progress >= 70) return '#10B981'; // green
    if (progress >= 40) return '#F59E0B'; // amber
    return '#EF4444'; // red
};

export const MyTeamGoalsList: React.FC<MyTeamGoalsListProps> = ({ data, cycleId }) => {
    const navigate = useNavigate();
    const items = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        return data.map(toTeamMemberGoal);
    }, [data]);

    if (items.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No goal data found
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-auto min-h-0 space-y-1" style={{ scrollbarWidth: 'none' }}>
                {items.map((member, index) => (
                    <div
                        key={String(member.userId) + index}
                        onClick={() => navigate(cycleId ? `/performspark/goal-management/cycle/${cycleId}/employee/${String(member.userId)}` : `/workforce/employee/${String(member.userId)}`)}
                        className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50/80 rounded-lg transition-colors cursor-pointer"
                    >
                        <div className="shrink-0">
                            <UserAvatar
                                name={member.employeeName}
                                userId={member.userId}
                                size="lg"
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">
                                {member.employeeName}
                            </div>

                            {/* Progress bar */}
                            <div className="mt-1 flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{
                                            width: `${Math.min(member.avgProgress, 100)}%`,
                                            backgroundColor: getProgressColor(member.avgProgress),
                                        }}
                                    />
                                </div>
                                <span className="text-[11px] text-gray-500 shrink-0 w-8 text-right">
                                    {Math.round(member.avgProgress)}%
                                </span>
                            </div>

                            {/* Goal counts */}
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[11px] text-gray-400">
                                    {member.totalGoals} goal{member.totalGoals !== 1 ? 's' : ''}
                                </span>
                                {member.onTrackGoals > 0 && (
                                    <span className="text-[11px] text-emerald-600">
                                        {member.onTrackGoals} on track
                                    </span>
                                )}
                                {member.atRiskGoals > 0 && (
                                    <span className="text-[11px] text-red-500">
                                        {member.atRiskGoals} at risk
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
